import { useMemo } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EventIcon from "@mui/icons-material/Event";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import BarChartIcon from "@mui/icons-material/BarChart";
import { useTranslationsContext } from "../../../contexts/TranslationsContext";
import type { Notification, NotificationType } from "../types";

/* ── type → icon + accent colour ───────────────────────────────────────── */

const TYPE_META: Record<
  NotificationType,
  { Icon: typeof EventIcon; color: string; bg: string }
> = {
  CLASS_REMINDER: {
    Icon: EventIcon,
    color: "#7aacb3",
    bg: "rgba(122,172,179,0.14)",
  },
  ATTENDANCE_MISSING: {
    Icon: PersonOffIcon,
    color: "#cf8da8",
    bg: "rgba(207,141,168,0.14)",
  },
  HOMEWORK_PENDING: {
    Icon: MenuBookIcon,
    color: "#e0a458",
    bg: "rgba(224,164,88,0.14)",
  },
  GRADE_PENDING: {
    Icon: BarChartIcon,
    color: "#b8a9e8",
    bg: "rgba(184,169,232,0.14)",
  },
};

/* ── helpers ────────────────────────────────────────────────────────────── */

/** Strip HTML tags and decode common HTML entities */
function stripHtml(raw: string): string {
  return raw
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&mdash;/g, "\u2014")
    .replace(/&ndash;/g, "\u2013")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function timeAgo(
  dateStr: string,
  labels: { now: string; m: string; h: string; d: string },
): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return labels.now;
  if (mins < 60) return `${mins}${labels.m}`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}${labels.h}`;
  const days = Math.floor(hrs / 24);
  return `${days}${labels.d}`;
}

/* ── component ─────────────────────────────────────────────────────────── */

type NotificationItemProps = {
  notification: Notification;
  onMarkRead: (id: number) => void;
  onDelete: (id: number) => void;
  /** CSS animation delay for stagger effect */
  animDelay?: number;
  /** Show skeleton overlay while deletion is in progress */
  isDeleting?: boolean;
};

export default function NotificationItem({
  notification,
  onMarkRead,
  onDelete,
  animDelay = 0,
  isDeleting = false,
}: NotificationItemProps) {
  const { translation } = useTranslationsContext();
  const meta = TYPE_META[notification.type] ?? TYPE_META.CLASS_REMINDER;
  const { Icon, color, bg } = meta;
  const isUnread = !notification.read;

  const cleanBody = useMemo(
    () => stripHtml(notification.body),
    [notification.body],
  );

  const timeLabels = {
    now: translation.NOTIFY_TIME_NOW?.defaultMessage || "now",
    m: translation.NOTIFY_TIME_MINUTES?.defaultMessage || "m",
    h: translation.NOTIFY_TIME_HOURS?.defaultMessage || "h",
    d: translation.NOTIFY_TIME_DAYS?.defaultMessage || "d",
  };

  /* ── Skeleton state while deleting ─────────────── */
  if (isDeleting) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.25,
          px: 1.5,
          py: 1.25,
          borderBottom: "1px solid rgba(255,255,255,0.03)",
          animation: "notifyPulse 1s ease-in-out infinite",
          opacity: 0.5,
        }}
      >
        <Skeleton
          variant="rounded"
          width={34}
          height={34}
          sx={{
            borderRadius: "10px",
            backgroundColor: "rgba(255,255,255,0.06)",
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton
            variant="text"
            width="70%"
            sx={{
              backgroundColor: "rgba(255,255,255,0.06)",
              fontSize: "0.78rem",
              mb: 0.3,
            }}
          />
          <Skeleton
            variant="text"
            width="90%"
            sx={{
              backgroundColor: "rgba(255,255,255,0.04)",
              fontSize: "0.72rem",
              mb: 0.15,
            }}
          />
          <Skeleton
            variant="text"
            width="40%"
            sx={{
              backgroundColor: "rgba(255,255,255,0.04)",
              fontSize: "0.72rem",
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      onClick={() => {
        if (isUnread) onMarkRead(notification.id);
      }}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.25,
        px: 1.5,
        py: 1.25,
        position: "relative",
        cursor: isUnread ? "pointer" : "default",
        transition: "background 0.2s, box-shadow 0.2s",
        backgroundColor: isUnread
          ? "rgba(122,172,179,0.05)"
          : "transparent",
        borderBottom: "1px solid rgba(255,255,255,0.03)",
        /* stagger fade-in */
        animation: "notifySlideIn 0.3s ease both",
        animationDelay: `${animDelay}ms`,
        /* unread left glow */
        ...(isUnread && {
          boxShadow: `inset 3px 0 0 0 ${color}`,
        }),
        "&:hover": {
          backgroundColor: "rgba(255,255,255,0.04)",
        },
        "&:hover .notify-delete-btn": {
          opacity: 1,
        },
      }}
    >
      {/* ── Type icon ─────────────────────────────── */}
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          mt: "2px",
          backgroundColor: bg,
          border: `1px solid ${color}22`,
        }}
      >
        <Icon sx={{ fontSize: 17, color }} />
      </Box>

      {/* ── Content ───────────────────────────────── */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 0.15,
          }}
        >
          <Tooltip title={notification.title} enterDelay={600}>
            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: isUnread ? 600 : 400,
                color: "#e0ddf0",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                flex: 1,
                lineHeight: 1.3,
              }}
            >
              {notification.title}
            </Typography>
          </Tooltip>
          {isUnread && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                backgroundColor: color,
                flexShrink: 0,
              }}
            />
          )}
        </Box>

        <Tooltip title={cleanBody} enterDelay={600}>
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: "#8a86a0",
              lineHeight: 1.45,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {cleanBody}
          </Typography>
        </Tooltip>

        <Typography
          sx={{
            fontSize: "0.63rem",
            color: "#5e5a74",
            mt: 0.4,
            letterSpacing: "0.02em",
          }}
        >
          {timeAgo(notification.createdAt, timeLabels)}
        </Typography>
      </Box>

      {/* ── Delete ────────────────────────────────── */}
      <Tooltip
        title={translation.NOTIFY_DELETE?.defaultMessage || "Delete"}
      >
        <IconButton
          className="notify-delete-btn"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(notification.id);
          }}
          sx={{
            color: "#5e5a74",
            flexShrink: 0,
            opacity: 0,
            transition: "opacity 0.15s, color 0.15s",
            mt: "2px",
            "&:hover": { color: "#cf8da8" },
          }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
